import { asCents } from '@m/utils/asCents';

import type {
  MarketParkingAvailabilityResponse,
  MarketParkingPriceDetail,
} from '@m/types/api/MarketParkingAvailability';

function centsEqual(left: number, right: number): boolean {
  return asCents(left) === asCents(right);
}

/**
 * `originalValue` is the base price. `bookingFee` is the synced Aeroparker fee.
 * `value` is the payable total (base + fee − discount) when that fee is part of
 * the charge.
 *
 * Event quotes already follow this: the daily average is `value + bookingFee`,
 * and `originalValue` is the base. Other quotes bake the fee into both `value`
 * and `originalValue`, so those totals match and the base is lost. For those,
 * `originalValue` becomes `originalValue − bookingFee`. `value` is left as the
 * payable total.
 */
export function normalizeQuotePrice(price: MarketParkingPriceDetail): MarketParkingPriceDetail {
  const fee = price.bookingFee ?? 0;
  if (fee <= 0) return price;

  const discount = price.discount ?? 0;
  const { averagePricePerDay, originalValue, value } = price;

  const feeOutsideQuotedTotals =
    centsEqual(averagePricePerDay, value + fee) ||
    centsEqual(averagePricePerDay, originalValue + fee);
  if (feeOutsideQuotedTotals) return price;

  const originalIncludesFee =
    centsEqual(originalValue, value) || centsEqual(originalValue, value + discount);
  const averageOmitsFee =
    averagePricePerDay > 0 &&
    (centsEqual(averagePricePerDay, value) || centsEqual(averagePricePerDay, originalValue));
  if (!originalIncludesFee || !averageOmitsFee) return price;

  const baseCents = asCents(originalValue) - asCents(fee);
  if (baseCents < 0) return price;

  return { ...price, originalValue: baseCents / 100 };
}

/** True when `value` is already base + booking fee − discount. */
export function quotePayableIncludesBookingFee(price: MarketParkingPriceDetail): boolean {
  const fee = price.bookingFee ?? 0;
  if (fee <= 0) return false;
  const discount = price.discount ?? 0;
  return centsEqual(price.value, price.originalValue + fee - discount);
}

/**
 * Net breakdown for the gated new order summary: parking cost = `netParkingPrice`,
 * service fee = `netBookingFee`, tax = `totalTax`. Only self-consistent when both net
 * amounts are present, so a partial payload returns `undefined` and callers keep the
 * legacy breakdown.
 */
export function getNetOrderSummaryBreakdown(
  price: MarketParkingPriceDetail,
): { parkingCost: number; serviceFee: number; tax: number } | undefined {
  const { netBookingFee, netParkingPrice, totalTax } = price;
  if (netParkingPrice == null || netBookingFee == null) return undefined;
  return { parkingCost: netParkingPrice, serviceFee: netBookingFee, tax: totalTax ?? 0 };
}

export function normalizeParkingAvailability(
  response: MarketParkingAvailabilityResponse,
): MarketParkingAvailabilityResponse {
  if (!response.zones?.length) return response;

  let changed = false;
  const zones = response.zones.map((zone) => {
    if (!zone.quotes?.length) return zone;

    let quotesChanged = false;
    const quotes = zone.quotes.map((quote) => {
      if (!quote.price) return quote;
      const price = normalizeQuotePrice(quote.price);
      if (price === quote.price) return quote;
      quotesChanged = true;
      changed = true;
      return { ...quote, price };
    });

    return quotesChanged ? { ...zone, quotes } : zone;
  });

  return changed ? { ...response, zones } : response;
}
